import { useEffect, useState } from 'react';
import { useMutation } from '@tanstack/react-query';
import { Mail, MessageSquare, Send, X } from 'lucide-react';
import { generateCareGuide, sendCareGuide, type CareGuideSendVia } from '@/lib/api/care-guides.js';
import { parseApiError } from '@/lib/api/errors.js';
import { toastError, toastSuccess } from '@/lib/toast.js';
import { cn } from '@/lib/utils.js';

export function SendCareGuideModal({
  open,
  onClose,
  ticketId,
  ticketNumber,
}: {
  open: boolean;
  onClose: () => void;
  ticketId: string;
  ticketNumber: string;
}) {
  const [sendEmail, setSendEmail] = useState(true);
  const [sendSms, setSendSms] = useState(true);
  const [previewHtml, setPreviewHtml] = useState<string | null>(null);
  const [previewText, setPreviewText] = useState<string | null>(null);

  const previewMut = useMutation({
    mutationFn: () => generateCareGuide(ticketId),
    onSuccess: (guide) => {
      setPreviewHtml(guide.html);
      setPreviewText(guide.text);
    },
    onError: (e) => toastError(parseApiError(e).message),
  });

  const sendMut = useMutation({
    mutationFn: () => {
      let via: CareGuideSendVia = 'both';
      if (sendEmail && !sendSms) via = 'email';
      else if (sendSms && !sendEmail) via = 'sms';
      else if (!sendEmail && !sendSms) {
        throw new Error('Select at least one delivery method');
      }
      return sendCareGuide(ticketId, { via });
    },
    onSuccess: (result) => {
      if (result.sent) {
        toastSuccess('Care guide sent to customer');
        onClose();
      } else {
        toastError('Care guide could not be sent — check customer contact info');
      }
    },
    onError: (e) => toastError(parseApiError(e).message),
  });

  useEffect(() => {
    if (open) {
      setPreviewHtml(null);
      setPreviewText(null);
      previewMut.mutate();
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [open, ticketId]);

  if (!open) return null;

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center p-4">
      <button
        type="button"
        aria-label="Close"
        className="absolute inset-0 bg-slate-950/40"
        onClick={onClose}
      />
      <div className="relative max-h-[90vh] w-full max-w-2xl overflow-hidden rounded-2xl bg-white shadow-2xl">
        <header className="flex items-center justify-between border-b border-slate-200 px-5 py-4">
          <div>
            <h2 className="text-lg font-bold text-slate-950">Send Care Guide</h2>
            <p className="text-sm text-slate-500">{ticketNumber}</p>
          </div>
          <button type="button" className="rounded-lg p-2 hover:bg-slate-100" onClick={onClose}>
            <X className="h-5 w-5" />
          </button>
        </header>

        <div className="max-h-[calc(90vh-8rem)] space-y-4 overflow-y-auto p-5">
          <div className="flex flex-wrap gap-4">
            <label className="flex cursor-pointer items-center gap-2 rounded-xl border border-slate-200 px-4 py-3 text-sm font-semibold">
              <input
                type="checkbox"
                checked={sendEmail}
                onChange={(e) => setSendEmail(e.target.checked)}
                className="rounded border-slate-300"
              />
              <Mail className="h-4 w-4 text-primary" />
              Email
            </label>
            <label className="flex cursor-pointer items-center gap-2 rounded-xl border border-slate-200 px-4 py-3 text-sm font-semibold">
              <input
                type="checkbox"
                checked={sendSms}
                onChange={(e) => setSendSms(e.target.checked)}
                className="rounded border-slate-300"
              />
              <MessageSquare className="h-4 w-4 text-accent" />
              SMS
            </label>
          </div>

          <section className="rounded-xl border border-slate-200 bg-slate-50 p-4">
            <h3 className="text-sm font-bold uppercase tracking-wide text-slate-500">Preview</h3>
            {previewMut.isPending ? (
              <p className="mt-2 text-sm text-slate-500">Generating preview…</p>
            ) : previewHtml ? (
              <div
                className="prose prose-sm mt-3 max-w-none rounded-lg bg-white p-4 ring-1 ring-slate-200"
                dangerouslySetInnerHTML={{ __html: previewHtml }}
              />
            ) : previewText ? (
              <pre className="mt-3 whitespace-pre-wrap rounded-lg bg-white p-4 text-sm text-slate-700 ring-1 ring-slate-200">
                {previewText}
              </pre>
            ) : (
              <p className="mt-2 text-sm text-slate-500">No preview available.</p>
            )}
          </section>
        </div>

        <footer className="flex justify-end gap-2 border-t border-slate-200 px-5 py-4">
          <button
            type="button"
            onClick={() => previewMut.mutate()}
            disabled={previewMut.isPending}
            className="rounded-xl border border-slate-300 px-4 py-2 text-sm font-semibold hover:bg-slate-50 disabled:opacity-50"
          >
            Refresh preview
          </button>
          <button
            type="button"
            disabled={sendMut.isPending || (!sendEmail && !sendSms)}
            onClick={() => sendMut.mutate()}
            className={cn(
              'inline-flex items-center gap-2 rounded-xl bg-primary px-4 py-2 text-sm font-bold text-white disabled:opacity-50',
            )}
          >
            <Send className="h-4 w-4" />
            {sendMut.isPending ? 'Sending…' : 'Send care guide'}
          </button>
        </footer>
      </div>
    </div>
  );
}
